﻿﻿import { DashRegistrationStep } from '../enums/index';
import { PreWorkupLabStep } from '../enums/index';
import { State } from '../enums/index';
import { Country } from '../enums/index';
import { Language } from '../enums/index';
import { ContactPreference } from '../enums/index';
import { ContactTimeHours } from '../enums/index';
import { MaritalStatus } from '../enums/index';
import { Ethnicity } from '../enums/index';
import { EmploymentStatus } from '../enums/index';
import { MedicalPto } from '../enums/index';
import { RelationshipToContact } from '../enums/index';
import { AnnualIncomeRange } from '../enums/index';
import { WageReimbursementImportance } from '../enums/index';
import { WageReimbursementStep } from '../enums/index';
import { EducationLevel } from '../enums/index';
import { ExerciseRoutine } from '../enums/index';
import { Gender } from '../enums/index';
import { DonorIntakeSituation } from '../enums/index';
import { PairCompatibilityStatus } from '../enums/index';
import { ReferralUrgency } from '../enums/index';
import { RelationshipToPatient } from '../enums/index';
import { FrequencyOfCommunication } from '../enums/index';
import { RemissionStatus } from '../enums/index';
import { DiabetesStatus } from '../enums/index';
import { KidneyStoneTreatment } from '../enums/index';
import { AboType } from '../enums/index';
import { MedicalHistoryStatus } from '../enums/index';
import { YesNoUnknown } from '../enums/index';
import { ResultsOfClimbingStairs } from '../enums/index';
import { StressTestResults } from '../enums/index';
import { Frequency } from '../enums/index';
import { Disposition } from '../enums/index';
import { ICenterStatisticsView } from './center-statistics-view';
import { IDonorReimbursement } from './donor-reimbursement';
import { IPreWorkupLabDetails } from './pre-workup-lab-details';
import { IDashProgressTracking } from './dash-progress-tracking';
import { IPostalCode } from './postal-code';
import { AlternateDonationOptions } from '../enums/index';
import { ICancerHistory } from './cancer-history';
import { ISurgicalHistory } from './surgical-history';
import { IMedicineUseHistory } from './medicine-use-history';
import { IDrugUseHistory } from './drug-use-history';
import { DashMicrositeMatchQuality } from './dash-microsite-match-quality';

export interface IDashRegistration {
    id: number;
    coordinatorContactId: number;
    micrositeRecipientId: number;
    centerId: number;
    center: ICenterStatisticsView;
    remoteCenterId: number;
    donorReimbursements: IDonorReimbursement[];
    entryDomain: string;
    isPrivateLabel: boolean;
    editKey: string;
    agreedToHipaa: boolean;
    currentRegistrationStep: DashRegistrationStep;
    currentRegistrationStepDesc: string,
    previousRegistrationStep: DashRegistrationStep;
    preWorkupLabStep: PreWorkupLabStep;
    preWorkupLabDetails: IPreWorkupLabDetails;
    isQuickStartDonor: boolean;
    remindersSent: number;
    isInEmr: boolean;
    isOnHold: boolean;
    isClosed: boolean;
    ruleOutReasons: string;
    stopCommunication: boolean;
    dashProgressTracking: IDashProgressTracking;
    isAcceptable: boolean;
    isCenterNotified: boolean;
    registrationDateTime: Date;
    expireDateTime: Date;
    historyCompleteDateTime: Date;
    emrRegistrationDateTime: Date;
    emrCompleteDateTime: Date;
    dispositionRecordedDateTime: Date;
    preliminaryReviewDateTime: Date;
    medicalReviewDateTime: Date;
    closedDateTime: Date;
    closedByUserId: number;
    onHoldReason: string;
    reviewComments: string;
    lastContactAttempt: Date;
    contactAttemptsMade: number;
    firstName: string;
    middleInitial: string;
    lastName: string;
    fullName: string;
    phoneNumber: string;
    phoneNumberSecondary: string;
    email: string;
    emergencyContact: string;
    emergencyContactPhone: string;
    referralSource: string;
    addressLine1: string;
    city: string;
    state: State;
    postalCode: string;
    postalCodeEntity: IPostalCode;
    countryOfResidence: Country;
    nonUsStateProvince: string;
    dlIssueCountry: Country;
    dlNonUsStateProvince: string;
    stateProvinceString: string;
    dlStateProvinceString: string;
    preferredLanguage: Language;
    contactPreference: ContactPreference;
    contactBestStartHour: ContactTimeHours;
    contactBestEndHour: ContactTimeHours;
    dlIssueState: State;
    dlNumber: string;
    dlNumberDecrypted: string;
    mothersMaiden: string;
    mothersMaidenDecrypted: string;
    ssn: string;
    ssnDecrypted: string;
    passportNum: string;
    passportNumDecrypted: string;
    maritalStatus: MaritalStatus;
    ethnicity: Ethnicity;
    employmentStatus: EmploymentStatus;
    occupation: string;
    occupationHasHeavyLifting: boolean;
    medicalPto: MedicalPto;
    isInterpreterNeeded: boolean;
    physicianName: string;
    physicianCity: string;
    physicianState: State;
    emergencyContactRelationship: RelationshipToContact;
    annualIncomeRange: AnnualIncomeRange;
    wageReimbursementImportance: WageReimbursementImportance;
    wantsWageReimbursement: boolean;
    wageReimbursementStep: WageReimbursementStep;
    wageReimbursementAmount: number;
    currentWeeklyGrossPay: number;
    incomePayStubGross: number;
    incomePayStubStartDate: Date;
    incomePayStubEndDate: Date;
    incomeIrsYearGross: number;
    incomeIrsYear: number;
    priorYearIncome: number;
    payStubFile: string;
    taxFile: string;
    estimatedWageReimbursementAmount: number;
    calculatedBusinessDayInPayStub: number;
    calculatedWeeklyPayByPayStub: number;
    calculatedWeeklyPayByYear: number;
    isAdp: boolean;
    isNdd: boolean;
    distanceFromCenter: number;
    educationLevel: EducationLevel;
    exerciseRoutine: ExerciseRoutine;
    hasHealthInsurance: boolean;
    gender: Gender;
    birthDate: Date;
    heightInches: number;
    weightLbs: number;
    isResidingInUsa: boolean;
    isAbleToGetToUsa: boolean;
    isDonatingForCompensation: boolean;
    donorSituation: DonorIntakeSituation;
    ddRecipFirstName: string;
    ddRecipLastName: string;
    ddKnowsRecipDob: boolean;
    compatibilityStatus: PairCompatibilityStatus;
    ddRecipDob: Date;
    ddRecipUrgency: ReferralUrgency;
    ddAlternateDonationOptionsBitwise: number;
    ddAlternateDonationOptions: AlternateDonationOptions[];
    ddRelationshipToPatient: RelationshipToPatient;
    ddFrequencyOfCommunication: FrequencyOfCommunication;
    recipientCenterId: number;
    recipientCenter: ICenterStatisticsView;
    recipientAtSameDashCenter: boolean;
    recipientIsWillingToTransfer: boolean;
    isHardshipToTravel: boolean;
    isRemoteDonationPreferred: boolean;
    whyNotRemote: string;
    motivationToDonate: string;
    willAcceptTransfusions: boolean;
    hasHadKidneyDisease: boolean;
    hasHadHeartAttack: boolean;
    heartAttackRemissionStatus: RemissionStatus;
    hasDiabetes: boolean;
    diabetesStatus: DiabetesStatus;
    hasHadCancer: boolean;
    cancersJson: string;
    cancers: ICancerHistory[];
    hasHadKidneyStones: boolean;
    kidneyStoneEpisodes: number;
    kidneyStoneRemissionStatus: RemissionStatus;
    kidneyStoneTreatment: KidneyStoneTreatment;
    hasBeenPregnant: boolean;
    isPregnant: boolean;
    isNursing: boolean;
    hadGestationalDiabetes: boolean;
    hasAllergies: boolean;
    hasHadSurgeries: boolean;
    takesMedication: boolean;
    allergiesJson: string;
    surgeriesJson: string;
    medicinesJson: string;
    allergies: string[];
    surgeries: ISurgicalHistory[];
    medicines: IMedicineUseHistory[];
    aboType: AboType;
    hasHistoryOfHighBp: MedicalHistoryStatus;
    highBpAgents: number;
    hadHighBpDuringPregnancy: MedicalHistoryStatus;
    hasHadCabg: YesNoUnknown;
    hasHistoryOfBloodClots: MedicalHistoryStatus;
    hasHistoryOfCoronaryDisease: MedicalHistoryStatus;
    hasHistoryOfHighCholOrTg: MedicalHistoryStatus;
    hasStents: YesNoUnknown;
    historyChestPainExercise: YesNoUnknown;
    historyChestPainOther: YesNoUnknown;
    resultsOfClimbingStairs: ResultsOfClimbingStairs;
    hadStressTestWithinYear: YesNoUnknown;
    stressTestLocation: string;
    stressTestResults: StressTestResults;
    hasHadStroke: YesNoUnknown;
    howManyStrokes: number;
    hasHadLegBypass: YesNoUnknown;
    howManyLegBypasses: number;
    historyLungDisease: MedicalHistoryStatus;
    hasCopd: MedicalHistoryStatus;
    hasTb: MedicalHistoryStatus;
    historyPneumonia: MedicalHistoryStatus;
    hasRespiratoryIssues: YesNoUnknown;
    respiratoryIssuesDescription: string;
    hasHiv: YesNoUnknown;
    hasGastroIssues: YesNoUnknown;
    gastroIssuesDescription: string;
    historyGallstones: MedicalHistoryStatus;
    historyPancreatitis: MedicalHistoryStatus;
    historyLiverDisease: MedicalHistoryStatus;
    hasHepB: MedicalHistoryStatus;
    hasHepC: MedicalHistoryStatus;
    hasBleedingProblem: MedicalHistoryStatus;
    hasHadUti: YesNoUnknown;
    utiEpisodes: number;
    utiRemissionStatus: RemissionStatus;
    historyKidneyDamage: MedicalHistoryStatus;
    historyProteinuria: MedicalHistoryStatus;
    historyHematuria: MedicalHistoryStatus;
    historyNeuroDisease: MedicalHistoryStatus;
    historySeizure: MedicalHistoryStatus;
    hasLupus: MedicalHistoryStatus;
    historyParalysis: MedicalHistoryStatus;
    hasArthritis: MedicalHistoryStatus;
    historyNeuropathy: MedicalHistoryStatus;
    historyDepression: MedicalHistoryStatus;
    historyAnxiety: MedicalHistoryStatus;
    hasHistoryOfPsychiatric: MedicalHistoryStatus;
    psychiatricDescription: string;
    historyFibroidUterus: MedicalHistoryStatus;
    historyEndometriosis: MedicalHistoryStatus;
    historyPolycysticOvaries: MedicalHistoryStatus;
    hasHadMiscarriages: YesNoUnknown;
    howManyMiscarriages: number;
    hasHadAbortions: YesNoUnknown;
    howManyAbortions: number;
    hasReceivedTransfusion: YesNoUnknown;
    howManyTransfusions: number;
    hasHistoryOfObesity: YesNoUnknown;
    hasFabrys: YesNoUnknown;
    hasSickleCell: YesNoUnknown;
    hasSickleCellTrait: YesNoUnknown;
    hasSarcoidosis: YesNoUnknown;
    hasAutoImmune: YesNoUnknown;
    autoImmuneDescription: string;
    lastPhysicalExam: string;
    hasHadPhysicalExam: YesNoUnknown;
    physicalExamYearsAgo: number;
    lastMammogram: string;
    hasHadMammogram: YesNoUnknown;
    mammogramYearsAgo: number;
    lastPapSmear: string;
    hasHadPapSmear: YesNoUnknown;
    papSmearYearsAgo: number;
    lastPsaTest: string;
    hasHadPsaTest: YesNoUnknown;
    psaTestYearsAgo: number;
    hasHadColonoscopy: YesNoUnknown;
    colonoscopyYearsAgo: number;
    additionalComments: string;
    hasHadSuicidalThoughtsAttempts: YesNoUnknown;
    familyHistoryKidneyDisease: YesNoUnknown;
    familyMembersKidneyDisease: string;
    familyHistoryHeartDisease: YesNoUnknown;
    familyMembersHeartDisease: string;
    familyHistoryCad: YesNoUnknown;
    familyMembersCad: string;
    familyHistoryBloodPressure: YesNoUnknown;
    familyMembersBloodPressure: string;
    familyHistoryCancer: YesNoUnknown;
    familyMembersCancer: string;
    familyHistoryKidneyCancer: YesNoUnknown;
    familyMembersKidneyCancer: string;
    familyHistoryDiabetes: YesNoUnknown;
    familyMembersDiabetes: string;
    isMotherAlive: YesNoUnknown;
    mothersLastAge: number;
    motherCauseOfDeath: string;
    isFatherAlive: YesNoUnknown;
    fathersLastAge: number;
    fatherCauseOfDeath: string;
    howManySiblings: number;
    howManyAdultChildren: number;
    howManyUnderAgeChildren: number;
    hasUsedAlcohol: boolean;
    usesAlcohol: boolean;
    alcoholDrinks: number;
    alcoholFrequency: Frequency;
    hasAbusedAlcohol: boolean;
    hasUsedTobacco: boolean;
    tobaccoYears: number;
    usesTobacco: boolean;
    willStopTobaccoUse: boolean;
    tobaccoPacks: number;
    tobaccoFrequency: Frequency;
    hasUsedDrugs: boolean;
    drugUseHistoryJson: string;
    drugUseHistory: IDrugUseHistory[];
    hasHadLegalIssues: boolean;
    hasBeenSentencedToPrison: boolean;
    isCurrentlyIncarcerated: boolean;
    prisonWhenAndWhere: string;
    hasTraveledLastYear: boolean;
    travelLocations: string;
    hasHadHighRiskSex: boolean;
    hasHadSexWithMmRelationsMale: boolean;
    hasHadMmSex: boolean;
    hasHadSexWithProstitute: boolean;
    hasProstituted: boolean;
    hasHadSexWithNeedleDrugUser: boolean;
    hasUsedNeedleDrugs: boolean;
    hasBeenInJailOver72: boolean;
    hasHadStd: boolean;
    hasBeenOnHemodialysis: boolean;
    registrationSiteName: string;
    isFromReferralSite: boolean;
    age: number;
    bmi: number;
    disposition: Disposition;
    donationTypeId: number,
    donationType: string
}
